import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { AssetProposal } from '../hooks/assetProposalUtils';
import AssetProposalTrigger, { AssetProposalTriggerHandle } from './AssetProposalTrigger';

interface AssetProposalGroupProps {
  proposals: AssetProposal[];
  conversationId: string;
  messageId: string;
  isAddMode: boolean;
}

const AssetProposalGroup: React.FC<AssetProposalGroupProps> = ({ proposals, conversationId, messageId, isAddMode }) => {
  const proposalStatuses = useSelector((state: RootState) => state.aiChat.proposalStatuses[conversationId] ?? {});
  const [isApprovingAll, setIsApprovingAll] = useState(false);
  const triggerRefs = useRef<(AssetProposalTriggerHandle | null)[]>([]);

  const messageIds = proposals.map((_, i) => `${messageId}-${i}`);
  const pendingIds = messageIds.filter((id) => !proposalStatuses[id] || proposalStatuses[id] === 'pending');
  const allSettled = pendingIds.length === 0;

  const handleApproveAll = async () => {
    setIsApprovingAll(true);
    // finally, so an unexpected throw can't strand every card disabled with no way back but a reload.
    try {
      await Promise.all(
        proposals.map(async (_, i) => {
          const id = messageIds[i];
          if (proposalStatuses[id] === 'approved' || proposalStatuses[id] === 'declined') return;
          await triggerRefs.current[i]?.approve();
        })
      );
    } finally {
      setIsApprovingAll(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-3">
      {proposals.length > 1 && !allSettled && !isAddMode && (
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={handleApproveAll}
            disabled={isApprovingAll}
            className="eai-btn-cta-toolbar"
          >
            {isApprovingAll && (
              <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            Approve All
          </button>
          <p className="text-xs text-eai-ink-subtle">{pendingIds.length} group{pendingIds.length !== 1 ? 's' : ''} still need{pendingIds.length === 1 ? 's' : ''} your approval</p>
        </div>
      )}

      {proposals.map((proposal, i) => (
        <AssetProposalTrigger
          key={messageIds[i]}
          ref={(el) => { triggerRefs.current[i] = el; }}
          proposal={proposal}
          conversationId={conversationId}
          messageId={messageIds[i]}
          disabled={isApprovingAll}
        />
      ))}
    </div>
  );
};

export default AssetProposalGroup;
